import { useState, useEffect, useCallback, useRef } from 'react';

interface ParallaxValues {
  rotateX: number;
  rotateY: number;
  x: number;
  y: number;
}

interface UseMouseParallaxOptions {
  maxRotation?: number;
  maxTranslation?: number;
  smoothing?: number;
  enabled?: boolean;
  disableOnMobile?: boolean;
  mobileBreakpoint?: number;
}

export function useMouseParallax(options: UseMouseParallaxOptions = {}) {
  const {
    maxRotation = 8,
    maxTranslation = 10,
    smoothing = 0.1,
    enabled = true,
    disableOnMobile = true,
    mobileBreakpoint = 768,
  } = options;

  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= mobileBreakpoint);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, [mobileBreakpoint]);

  const isEnabled = enabled && !(disableOnMobile && isMobile);

  const [parallax, setParallax] = useState<ParallaxValues>({
    rotateX: 0,
    rotateY: 0,
    x: 0,
    y: 0,
  });

  const targetRef = useRef<ParallaxValues>({
    rotateX: 0,
    rotateY: 0,
    x: 0,
    y: 0,
  });

  const animationFrameRef = useRef<number | undefined>(undefined);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isEnabled) return;

      const { clientX, clientY } = event;
      const { innerWidth, innerHeight } = window;

      // Normalize mouse position to -1 to 1
      const normalizedX = (clientX / innerWidth) * 2 - 1;
      const normalizedY = (clientY / innerHeight) * 2 - 1;

      // Calculate target rotation (inverted for natural feel)
      targetRef.current = {
        rotateX: -normalizedY * maxRotation,
        rotateY: normalizedX * maxRotation,
        x: normalizedX * maxTranslation,
        y: normalizedY * maxTranslation,
      };
    },
    [isEnabled, maxRotation, maxTranslation]
  );

  const handleMouseLeave = useCallback(() => {
    targetRef.current = {
      rotateX: 0,
      rotateY: 0,
      x: 0,
      y: 0,
    };
  }, []);

  // Smooth animation loop
  useEffect(() => {
    if (!isEnabled) {
      // Reset to zero when disabled
      setParallax({ rotateX: 0, rotateY: 0, x: 0, y: 0 });
      targetRef.current = { rotateX: 0, rotateY: 0, x: 0, y: 0 };
      return;
    }

    const animate = () => {
      setParallax((prev) => ({
        rotateX: prev.rotateX + (targetRef.current.rotateX - prev.rotateX) * smoothing,
        rotateY: prev.rotateY + (targetRef.current.rotateY - prev.rotateY) * smoothing,
        x: prev.x + (targetRef.current.x - prev.x) * smoothing,
        y: prev.y + (targetRef.current.y - prev.y) * smoothing,
      }));

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isEnabled, smoothing]);

  // Add event listeners
  useEffect(() => {
    if (!isEnabled) return;

    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isEnabled, handleMouseMove, handleMouseLeave]);

  // Generate CSS transform string
  const getTransformStyle = (intensity = 1) => ({
    transform: `perspective(1000px) rotateX(${parallax.rotateX * intensity}deg) rotateY(${parallax.rotateY * intensity}deg)`,
    transition: 'transform 0.1s ease-out',
  });

  // Generate CSS transform with translation
  const getTransformWithTranslate = (intensity = 1, translateIntensity = 1) => ({
    transform: `perspective(1000px) rotateX(${parallax.rotateX * intensity}deg) rotateY(${parallax.rotateY * intensity}deg) translateX(${parallax.x * translateIntensity}px) translateY(${parallax.y * translateIntensity}px)`,
    transition: 'transform 0.1s ease-out',
  });

  return {
    parallax,
    getTransformStyle,
    getTransformWithTranslate,
  };
}
